/**
 * AiGenerateEntityComponent handles entity component generation through AI chat interface.
 * Manages component creation workflow, state tracking, and user interactions.
 * Provides specialized functionality for entity-specific component generation.
 */
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { AiAbstractChatComponent } from '../../ai-abstract-chat/ai-abstract-chat.component';
import { AIChatGeneratorService } from '../../../services/ai-generator.service';
import { AIPromptService } from '../../../services/ai-prompt.service';
import { ProjectChannelMessage, ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectActiveProjectId } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';
import { selectActivePerson } from 'libs/authentication/src/lib/state/authentication.selectors';
import * as ProjectModelActions from 'libs/project/src/lib/states/project-model-state/project-model.actions';
import { aiFunctionIds as AI_FUNCTION_IDS, AiChatConfig, AIGenerationType } from '../../../utils/ai-response-types.interface';
import { MessageType } from '@rappider/rappider-components/utils';
import { getProjectModelsWithFields } from '@rappider/shared';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { selectComponentDefinitionById } from 'libs/component-definition/src/lib/state/component-definition.selectors';
import { ComponentDefinitionWithRelations } from '@rappider/rappider-sdk';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { AI_CHAT_CONFIGS } from '../../../configs/ai-chat-config';
import { ComponentGeneratorService } from 'libs/content-editor/src/lib/services/component-generator.service';
import { EntityComponentStateService } from '../../../services/entity-component-state.service';
import { EntityVisualizationService } from '../../../services/entity-visualization.service';
import { AiErrorHandlerService } from '../../../services/ai-error-handler.service';

@Component({
  selector: 'rappider-ai-generate-entity-component',
  standalone: true,
  imports: [
    CommonModule,
    AiAbstractChatComponent,
    NzButtonModule,
    NzSkeletonModule
  ],
  templateUrl: './ai-generate-entity-component.component.html',
  styleUrls: ['./ai-generate-entity-component.component.scss'],
})
export class AiGenerateEntityComponent implements OnInit, OnDestroy, OnChanges {

  @Input() projectId?: string;

  @ViewChild('abstractChat') abstractChat!: AiAbstractChatComponent & {
    messages: ProjectChannelMessage[];
    openSplitter: () => void;
  };

  activeProjectId?: string;
  activePersonId?: string;
  selectedProjectModelId?: string | undefined;
  componentDefinitionId?: string | undefined;
  componentDefinition: ComponentDefinitionWithRelations | undefined;
  aiFunctionIds = AI_FUNCTION_IDS;
  projectModels: ProjectModelWithRelations[] | undefined;
  selectedProjectModel: ProjectModelWithRelations | undefined;

  // Flag to indicate if the selected model was not found even though we have project models
  selectedProjectModelNotFound = false;

  subscriptions: Subscription[] = [];

  uploadedFiles: File[] = [];

  private destroy$ = new Subject<void>();
  private loadingTimeout: NodeJS.Timeout | null = null;
  private readonly AI_SENDER_ID = '00000000-0000-0000-0000-000000000001';

  configType = AIGenerationType.ENTITY_COMPONENT;
  aiStartConfig: AiChatConfig = AI_CHAT_CONFIGS[this.configType];

  // Custom example prompts for entity component generation
  get examplePrompts(): string[] {
    if (this.hasDisplayComponent || this.componentDefinitionId) {
      return [];
    }
    return [
      'Design a product card that displays a primary image, the item name, its price, and a star rating. The card should have an "Add to Cart" button and a small heart icon for adding to a wishlist.',
      'Create a compact list item for a task, showing its title, due date, and the avatar of the assigned user. Include a colored tag to indicate priority (e.g., High, Medium, Low) and a checkbox to mark it as complete.',
      'Generate a user profile card for a directory, which includes the user\'s avatar, full name, job title, and an online status indicator. It needs to have two action buttons: "Send Message" and "View Profile".',
      'Create a card for a restaurant discovery app that features a large photo of the restaurant, its name, cuisine type, and price range (e.g., $$). It must also show the average user review score and have a clear "Book a Table" call-to-action button.'
    ];
  }

  /**
   * Check if displayWithComponent exists in entity config and has a valid component definition ID
   */
  get hasDisplayComponent(): boolean {
    if (!this.selectedProjectModel) {
      return false;
    }

    const componentDefinitionId = this.entityVisualizationService.getComponentDefinitionId(this.selectedProjectModel);
    return !!componentDefinitionId;
  }

  /**
   * Check if canvas should be open (componentDefinitionId exists or hasDisplayComponent is true)
   */
  get shouldOpenCanvas(): boolean {
    return !!this.componentDefinitionId || this.hasDisplayComponent;
  }

  constructor(
    private aiChatGeneratorService: AIChatGeneratorService,
    private aiPromptService: AIPromptService,
    private componentGeneratorService: ComponentGeneratorService,
    private entityVisualizationService: EntityVisualizationService,
    private entityComponentStateService: EntityComponentStateService,
    private aiErrorHandlerService: AiErrorHandlerService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    // Constructor intentionally left empty
  }

  ngOnInit(): void {
    this.subscriptions = [
      this.subscribeToRouteParameters(),
      this.subscribeToActiveProject(),
      this.subscribeToActivePerson(),
      this.subscribeToProjectModels(),
      ...this.subscribeToAIActions()
    ];
    // Subscribe to component definition after route params are processed
    if (this.componentDefinitionId) {
      this.subscriptions.push(this.subscribeToComponentDefinition());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedProjectModel'] || changes['componentDefinitionId']) {
      this.updateAiStartConfig();
    }
  }

  ngOnDestroy(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    this.subscriptions.forEach(subscription => subscription.unsubscribe());

    this.destroy$.next();
    this.destroy$.complete();
  }


  /**
   * Handle response action from AI component generation
   * @param event - Event containing action type and data
   */
  onResponseAction(event: { type?: string; data?: unknown; [key: string]: unknown }): void {
    let responseData: Record<string, unknown>;

    if (event.type === 'save' && event.data) {
      responseData = event.data as Record<string, unknown>;
    } else if ((event as Record<string, unknown>).componentDefinition) {
      responseData = event as Record<string, unknown>;
    } else {
      return;
    }

    const componentDefinition = responseData.componentDefinition as Record<string, unknown>;
    const componentDefinitionId = componentDefinition?.id as string;

    if (componentDefinitionId && this.selectedProjectModelId) {
      this.aiChatGeneratorService.notifyApproveBranchSuccess(componentDefinitionId);
    }
  }

  /**
   * Handle splitter toggle request for navigation
   * @param shouldOpen - Whether the splitter should be opened
   */
  onSplitterToggleRequested(shouldOpen: boolean | Event): void {
    const shouldOpenBoolean = typeof shouldOpen === 'boolean' ? shouldOpen : (shouldOpen as any).detail;
    if (shouldOpenBoolean) {
      this.router.navigate(['/ai/entity-visualization', this.selectedProjectModelId]);
    }
  }

  /**
   * Handle message sent event from chat interface
   * @param event - Event containing message text and optional files
   */
  public onMessageSent(event: { message: string; files?: File[] }): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    this.loadingTimeout = setTimeout(() => {
      this.abstractChat.isTyping = true;
    }, 500);

    if (event.files) {
      this.uploadedFiles = event.files;
    }

    const contextualPrompt = this.aiPromptService.buildEntityComponentContextualPrompt(
      event.message,
      this.abstractChat.messages,
      this.selectedProjectModel
    );
    this.generateComponentForEntity(contextualPrompt);
  }

  /**
   * Generate component for entity using AI service
   * @param prompt - The prompt text for component generation
   */
  private generateComponentForEntity(prompt: string) {
    const projectId = this.projectId || this.activeProjectId;
    if (!projectId) {
      this.clearLoadingState();
      this.addErrorMessageToChat('No active project selected. Please select a project to continue.');
      return;
    }

    const files = this.uploadedFiles;
    const description = this.aiPromptService.buildEntityComponentDescription(
      prompt,
      this.selectedProjectModel,
      files
    );

    if (this.componentDefinitionId) {
      this.updateExistingComponent(description, files);
    } else if (this.hasDisplayComponent && this.selectedProjectModel) {
      this.updateExistingComponent(description, files);
    } else {
      this.createNewComponent(description, files);
    }
  }

  /**
   * Create new component using the standard generation endpoint
   * @param description - The component description
   * @param files - Optional files to attach
   */
  private createNewComponent(description: string, files: File[]) {
    const componentFramework = 'flutter';
    const isEntityComponent = true;
    const relatedProjectModelId = this.selectedProjectModel?.id;

    const subscription = this.componentGeneratorService.generateComponent(
      description,
      undefined,
      undefined,
      files,
      componentFramework,
      isEntityComponent,
      relatedProjectModelId
    ).subscribe(
      (response: unknown) => {
        this.clearLoadingState();
        const responseData = response as Record<string, unknown>;
        let componentDefId: string | undefined;
        let componentCompilationId: string | undefined;

        if (responseData?.componentDefinition) {
          const componentDef = responseData.componentDefinition as Record<string, unknown>;
          componentDefId = componentDef?.id as string;
          componentCompilationId = responseData?.componentCompilationId as string;
        } else if (responseData?.componentId) {
          componentDefId = responseData.componentId as string;
          componentCompilationId = (responseData?.stepId as string) || (responseData?.draftId as string);
        }

        if (componentDefId) {
          const messageId = Date.now().toString();
          const draftId = responseData?.draftId as string;
          const stepId = responseData?.stepId as string;
          const normalizedResponse = {
            ...responseData,
            componentDefinition: responseData.componentDefinition || { id: componentDefId },
            componentCompilationId: componentCompilationId || responseData?.componentCompilationId,
            messageId: messageId
          };
          // Use route componentDefinitionId if available (update mode), otherwise use the generated one
          const finalComponentDefId = this.componentDefinitionId || componentDefId;
          this.addAIResponseToChat(normalizedResponse, messageId, finalComponentDefId);

          if (componentCompilationId) {
            this.entityComponentStateService.startComponentGeneration(
              componentDefId,
              componentCompilationId,
              messageId,
              draftId,
              stepId
            );
          }
          return;
        }

        this.addErrorMessageToChat('Component generation failed: Missing component definition');
      },
      error => {
        this.clearLoadingState();
        const errorMessage = this.aiErrorHandlerService.extractErrorMessage(error);
        this.addErrorMessageToChat(errorMessage);
      }
    );
    this.subscriptions.push(subscription);
  }

  /**
   * Update existing component using the update-by-ai endpoint
   * @param description - The component description
   * @param files - Optional files to attach
   */
  private updateExistingComponent(description: string, files: File[]) {
    const componentDefinitionId = this.componentDefinitionId ||
      this.entityVisualizationService.getComponentDefinitionId(this.selectedProjectModel || null);

    if (!componentDefinitionId) {
      this.clearLoadingState();
      this.addErrorMessageToChat('Component definition ID not found for update');
      return;
    }

    const subscription = this.componentGeneratorService.updateComponentByAI(
      componentDefinitionId,
      description,
      'flutter',
      files
    ).subscribe(
      (response: unknown) => {
        this.clearLoadingState();
        const responseData = response as Record<string, unknown>;

        let componentDefId: string | undefined;
        let componentCompilationId: string | undefined;

        if (responseData?.componentDefinition) {
          // Old format: { componentDefinition: { id: "..." }, componentCompilationId: "..." }
          const componentDef = responseData.componentDefinition as Record<string, unknown>;
          componentDefId = componentDef?.id as string;
          componentCompilationId = responseData?.componentCompilationId as string;
        } else if (responseData?.componentId) {
          componentDefId = responseData.componentId as string;
          componentCompilationId = (responseData?.stepId as string) || (responseData?.draftId as string);
        } else {
          // New format: { draftId: "...", stepId: "...", summary: "..." }
          // For update operations, use the existing componentDefinitionId
          componentDefId = componentDefinitionId;
          componentCompilationId = (responseData?.draftId as string) || (responseData?.stepId as string);
        }

        if (componentDefId) {
          const messageId = Date.now().toString();
          const normalizedResponse = {
            ...responseData,
            componentDefinition: responseData.componentDefinition || { id: componentDefId },
            componentCompilationId: componentCompilationId || responseData?.componentCompilationId,
            draftId: responseData?.draftId || componentCompilationId,
            stepId: responseData?.stepId,
            messageId: messageId
          };
          // Use route componentDefinitionId if available (update mode), otherwise use the generated one
          const finalComponentDefId = this.componentDefinitionId || componentDefId;
          this.addAIResponseToChat(normalizedResponse, messageId, finalComponentDefId);

          if (componentCompilationId) {
            this.entityComponentStateService.startComponentGeneration(componentDefId, componentCompilationId, messageId);
          }
          return;
        }

        this.addErrorMessageToChat('Component update failed: Missing component definition');
      },
      error => {
        this.clearLoadingState();
        const errorMessage = this.aiErrorHandlerService.extractErrorMessage(error);
        this.addErrorMessageToChat(errorMessage);
      }
    );
    this.subscriptions.push(subscription);
  }

  /**
   * Subscribe to active project changes
   * @returns Subscription for active project
   */
  private subscribeToActiveProject() {
    return this.store.select(selectActiveProjectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(projectId => {
        this.activeProjectId = projectId;
      });
  }

  /**
   * Subscribe to route parameter changes
   * @returns Subscription for route parameters
   */
  subscribeToRouteParameters() {
    return this.route.paramMap.subscribe((params: ParamMap) => {
      const routeComponentDefinitionId = params.get('componentDefinitionId');
      const routeProjectModelId = params.get('projectModelId');

      if (routeComponentDefinitionId) {
        this.componentDefinitionId = routeComponentDefinitionId;
        this.selectedProjectModelId = undefined;
        this.componentDefinition = undefined;
        this.subscriptions.push(this.subscribeToComponentDefinition());
      } else if (routeProjectModelId) {
        this.selectedProjectModelId = routeProjectModelId;
        this.componentDefinitionId = undefined;
        this.componentDefinition = undefined;
      }

      this.selectProjectModel();
    });
  }

  /**
   * Subscribe to project models changes
   * @returns Subscription for project models
   */
  subscribeToProjectModels() {
    return this.store.select(getProjectModelsWithFields).subscribe((projectModels: ProjectModelWithRelations[] | undefined) => {
      this.projectModels = projectModels;
      this.selectProjectModel();
    });
  }

  /**
   * Subscribe to component definition changes when componentDefinitionId is set
   * @returns Subscription for component definition
   */
  private subscribeToComponentDefinition(): Subscription {
    if (!this.componentDefinitionId) {
      return new Subscription();
    }

    return this.store.select(selectComponentDefinitionById(this.componentDefinitionId))
      .subscribe((componentDefinition: ComponentDefinitionWithRelations | undefined) => {
        this.componentDefinition = componentDefinition;
        if (componentDefinition) {
          this.updateAiStartConfig();
        }
      });
  }

  /**
   * Select project model by ID or component definition ID
   */
  private selectProjectModel(): void {
    if (!this.projectModels?.length) {
      this.selectedProjectModel = undefined;
      this.selectedProjectModelNotFound = !!this.selectedProjectModelId || !!this.componentDefinitionId;
      return;
    }

    if (this.componentDefinitionId) {
      this.selectedProjectModel = this.projectModels.find(pm =>
        this.entityVisualizationService.getComponentDefinitionId(pm) === this.componentDefinitionId
      );
      if (this.selectedProjectModel) {
        this.selectedProjectModelId = this.selectedProjectModel.id;
      }
    } else if (this.selectedProjectModelId) {
      this.selectedProjectModel = this.projectModels.find(pm => pm.id === this.selectedProjectModelId);
    }

    this.selectedProjectModelNotFound = !this.selectedProjectModel && (!!this.selectedProjectModelId || !!this.componentDefinitionId);
    this.updateAiStartConfig();
  }

  /**
   * Update AI start configuration based on current context
   */
  private updateAiStartConfig(): void {
    if (!this.selectedProjectModel && !this.componentDefinitionId) {
      return;
    }

    const entityName = this.selectedProjectModel?.name || 'Component';
    const componentName = this.componentDefinition?.title || 'Component';
    const isUpdateMode = this.componentDefinitionId || this.hasDisplayComponent;

    if (isUpdateMode) {
      this.aiStartConfig = {
        ...this.aiStartConfig,
        title: this.componentDefinitionId ? `Update ${componentName} with AI` : `Update Visual Element for ${entityName} Entity Items`,
        description: 'Describe the changes you want to make to improve the component design, add new features, ' +
          'or modify the existing layout. The AI will help you enhance your current component using the update endpoint.',
        placeholder: this.componentDefinitionId
          ? `Describe the improvements you want to make to ${componentName} (e.g., 'Add a new field' or 'Change the layout')`
          : `Describe the improvements (e.g., 'Add a status indicator to the ${entityName} card' or 'Make layout more compact')`,
        buttonText: 'Update Component',
        saveButtonText: 'Save Changes',
        saveButtonIcon: 'fa-solid fa-save'
      };
    } else {
      this.aiStartConfig = {
        ...this.aiStartConfig,
        title: `Design a Visual Element for ${entityName} Entity Items`,
        description: 'Create a reusable card that the AI will use to display your data. ' +
          'When you ask the AI about a specific item (like a patient, product, or task), ' +
          'it will use the component you design here. Describe how a single item should look ' +
          'and what actions it should have. e.g., A patient card with their name, photo, ' +
          'and primary diagnosis, with a button to "View Full Chart".',
        placeholder: 'Describe the entity component you want to create (e.g., "Create a card ' +
          'component for displaying patient information with name, photo, and diagnosis")',
        buttonText: 'Generate Entity Component',
        saveButtonText: 'Save',
        saveButtonIcon: 'fa-solid fa-save'
      };
    }
  }

  /**
   * Subscribe to active person changes
   * @returns Subscription for active person
   */
  private subscribeToActivePerson() {
    return this.store.select(selectActivePerson)
      .pipe(takeUntil(this.destroy$))
      .subscribe(activePerson => {
        this.activePersonId = activePerson?.id;
      });
  }

  /**
   * Subscribe to AI generation actions and responses
   * @returns Array of subscriptions for AI actions
   */
  private subscribeToAIActions() {
    return [
      this.aiChatGeneratorService.subscribeToAIGenerationSuccess()
        .pipe(takeUntil(this.destroy$))
        .subscribe((action: unknown) => {
          if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = null;
          }
          this.abstractChat.isTyping = false;
          const actionData = action as Record<string, unknown>;
          const payload = actionData.payload as Record<string, unknown>;
          const messageId = payload?.messageId as string || Date.now().toString();
          const componentId = payload?.componentId as string ||
                              (payload?.componentDefinition as Record<string, unknown>)?.id as string ||
                              this.componentDefinitionId ||
                              '';
          // Use route componentDefinitionId if available (update mode), otherwise use the payload one
          const finalComponentId = this.componentDefinitionId || componentId;
          this.addAIResponseToChat(payload, messageId, finalComponentId);
        }),

      this.aiChatGeneratorService.subscribeToAIGenerationError()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout);
            this.loadingTimeout = null;
          }
          this.abstractChat.isTyping = false;
        }),

      this.aiChatGeneratorService.subscribeToSaveSuccess()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.store.dispatch(new ProjectModelActions.GetProjectModels());
        })
    ];
  }

  /**
   * Add AI response to chat interface
   * @param responseData - Response data from AI generation
   * @param messageId - Message ID for state tracking
   * @param componentDefinitionId - Component definition ID
   */
  private addAIResponseToChat(
    responseData: unknown,
    messageId: string,
    componentDefinitionId: string
  ): void {
    this.clearLoadingState();
    const entityComponentResponse = responseData as Record<string, unknown>;

    if (!entityComponentResponse?.componentDefinition && !entityComponentResponse?.componentId) {
      return;
    }

    const isEmptyEntityResponse = !this.entityComponentStateService.validateEntityComponentResponse(entityComponentResponse);

    const aiMessage = this.aiChatGeneratorService.createAIResponseMessage(
      entityComponentResponse,
      {
        messageId,
        projectId: this.activeProjectId || 'unknown-project',
        projectModelId: this.selectedProjectModelId,
        componentDefinitionId: componentDefinitionId,
        responseType: AIGenerationType.ENTITY_COMPONENT,
        isEmptyResponse: isEmptyEntityResponse,
        senderId: this.AI_SENDER_ID
      }
    );

    if (this.abstractChat) {
      this.abstractChat.addAIResponse(aiMessage);
    }
  }

  /**
   * Navigate to project model selection page
   */
  navigateToProjectModelSelection() {
    if (this.activeProjectId) {
      this.router.navigate(['/ai/manage-ui']);
    }
  }

  /**
   * Clear loading state when error occurs or operation completes
   */
  private clearLoadingState(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    if (this.abstractChat) {
      this.abstractChat.isTyping = false;
      this.abstractChat.aiCallInProgress = false;
    }
  }

  /**
   * Add error message to chat interface
   * @param message - Error message to display
   */
  private addErrorMessageToChat(message: string): void {
    this.clearLoadingState();

    const aiMessage = {
      id: Date.now().toString(),
      message: message,
      date: new Date().toISOString(),
      channelId: 'ai-channel',
      projectId: this.activeProjectId || 'unknown-project',
      senderId: this.AI_SENDER_ID,
      status: 'new',
      responseData: null,
      responseType: AIGenerationType.ENTITY_COMPONENT,
      isEmptyResponse: false,
      type: MessageType.AI,
    };

    if (this.abstractChat) {
      this.abstractChat.addAIResponse(aiMessage as ProjectChannelMessage);
    }
  }

  /**
   * Handle revert request from chat interface
   * Removes all subsequent messages (both user and AI) after the reverted step
   * @param event - Event containing messageId, draftId, and stepId
   */
  onRevertRequested(event: { messageId: string; draftId: string; stepId: string }): void {
    if (!this.abstractChat || !this.abstractChat.messages) {
      return;
    }

    const messages = this.abstractChat.messages;
    const revertedMessageIndex = messages.findIndex(
      msg => msg.id === event.messageId || msg.messageId === event.messageId
    );

    if (revertedMessageIndex === -1) {
      console.warn('Reverted message not found in messages array');
      return;
    }

    this.abstractChat.messages = messages.slice(0, revertedMessageIndex + 1);
  }

}
